import { useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Fade,
  Stack,
  useTheme,
  Autocomplete,
  TextField
} from "@mui/material"
import { useTranslation } from "react-i18next"
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Store as StoreIcon,
  Save as SaveIcon
} from "@mui/icons-material"
import { useForm, Controller } from "react-hook-form"
import {
  useCreateLocationMutation,
  useUpdateLocationMutation,
  LocationType,
  Status,
  LocationDto,
  FilterOperator,
  useGetLocationForDetailsQuery
} from "src/graphql/quark/quark.type"
import OutlinedInput from "src/shared/components/outlined-input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { LOCATION_STATUS_OPTIONS, LOCATION_TYPE_OPTIONS } from "../../constants"
import SelectCountryField from "src/shared/container/select-country-field"
import SelectCityField from "src/shared/container/select-city-field"
import SelectLocationField from "src/shared/container/select-location-field"
import { Conditional } from "src/shared/components"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { LocationFormDialogSkeleton } from "./LocationFormSkeleton"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

const locationSchema: yup.ObjectSchema<LocationFormData> = yup.object().shape({
  name: yup.string().required("Location name is required"),
  type: yup.mixed<LocationType>().oneOf(Object.values(LocationType)).required("Location type is required"),
  status: yup.mixed<Status>().oneOf(Object.values(Status)).required("Location status is required"),
  area: yup.string().required("Area is required"),
  cityId: yup.string().required("City is required"),
  taxNumber: yup.string().optional(),
  salesTaxNumber: yup.string().optional(),
  warehouse_id: yup.string().required("Warehouse is required"),
  countryId: yup.string().required("Country is required"),
  timezone: yup.string().optional().nullable()
})

type LocationFormData = {
  name: string
  type: LocationType
  status: Status
  countryId?: string | null
  area?: string | null
  cityId?: string | null
  taxNumber?: string | null
  salesTaxNumber?: string | null
  warehouse_id?: string | null
  timezone?: string | null
}

const TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(key: "timeZone"): string[] }).supportedValuesOf("timeZone")
  } catch {
    return ["Asia/Karachi"]
  }
})()

const initialValues = {
  name: "",
  type: LocationType.Retail,
  status: Status.Active,
  countryId: "",
  area: "",
  cityId: "",
  taxNumber: "",
  salesTaxNumber: "",
  warehouse_id: "",
  timezone: ""
}
const EditUpdateLocationFormModal = () => {
  const theme = useTheme()
  const { t } = useTranslation("system")
  // const [selectedCountry, setSelectedCountry] = useState<string>("")
  const [searchParams, setSearchParams] = useSearchParams()
  const locationId = searchParams.get("id")
  const formMode = searchParams.get("mode")
  const isOpen = !!formMode
  const { data, loading: isGetLocationLoading } = useGetLocationForDetailsQuery({
    variables: { getLocationId: locationId as string },
    skip: !locationId
  })
  const location = data?.getLocation

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty }
  } = useForm<LocationFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(locationSchema)
  })

  const [createLocation, { loading: createLoading }] = useCreateLocationMutation()
  const [updateLocation, { loading: updateLoading }] = useUpdateLocationMutation()

  const watchedType = watch("type")
  const watchedStatus = watch("status")
  const watchedCountryId = watch("countryId")

  useEffect(() => {
    if (location && formMode === "edit") {
      reset({
        name: location.name || "",
        type: location.type || LocationType.Retail,
        status: (location.status as Status) || Status.Active,
        area: location.area || "",
        cityId: location.cityId || "",
        taxNumber: location.taxNumber || "",
        salesTaxNumber: location.salesTaxNumber || "",
        warehouse_id: location.warehouseId || "",
        timezone: location.timezone || "",
        ...(location.city?.country?.id && { countryId: location.city?.country?.id || "" })
      })
      if (location.city?.country?.id) {
        // setSelectedCountry(location.city.country.id)
      }
    } else if (formMode === "create") {
      reset(initialValues)
      // setSelectedCountry("")
    }
  }, [location, formMode, reset])

  const onSubmit = async (data: LocationFormData) => {
    try {
      const locationDto: LocationDto = {
        name: data.name,
        type: data.type,
        status: data.status,
        area: data.area || undefined,
        cityId: data.cityId || undefined,
        taxNumber: data.taxNumber || undefined,
        salesTaxNumber: data.salesTaxNumber || undefined,
        warehouse_id: data.warehouse_id || undefined,
        // Empty clears the override, so the tenant's timezone applies again.
        timezone: data.timezone ?? ""
      }

      if (formMode === "create") {
        const resp = await createLocation({
          variables: { locationDto },
          refetchQueries: ["GetLocationsList"]
        })
        resp.data?.createLocation.id
          ? toast.success("Location created successfully")
          : toast.error("Failed to create location")
      } else {
        const resp = await updateLocation({
          variables: {
            locationDto,
            updateLocationId: location?.id as string
          },
          refetchQueries: ["GetLocationsList"]
        })
        resp.data?.updateLocation
          ? toast.success("Location updated successfully")
          : toast.error("Failed to update location")
      }
      handleClose()
    } catch (error) {
      console.error("Error saving location:", error)
    }
  }

  const handleClose = () => {
    reset(initialValues)
    // setSelectedCountry("")
    setSearchParams({})
  }

  const getTypeIcon = (type: string) => {
    return type === "WAREHOUSE" ? <BusinessIcon /> : <StoreIcon />
  }

  const getStatusColor = (status: string) => {
    return status === "ACTIVE" ? "success" : "warning"
  }

  const getTypeColor = (type: string) => {
    return type === "WAREHOUSE" ? "primary" : "secondary"
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "600px"
        }
      }}
    >
      <Conditional condition={!isGetLocationLoading} elseBlock={<LocationFormDialogSkeleton />}>
        <DialogTitle
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: theme.palette.primary.contrastText,
            py: 3,
            px: 4,
            position: "relative"
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {formMode === "create" ? "Create New Location" : "Edit Location"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {formMode === "create" ? "Add a new location to your system" : "Update location information"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: theme.palette.primary.contrastText
              // backgroundColor: theme.palette.action,
              // "&:hover": {
              // backgroundColor: theme.palette.action.selected
              // }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ background: theme.palette.background.default }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid2 container spacing={3} sx={{ py: 4 }}>
              {/* Basic Information Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <LocationIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Basic Information
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Location Name"
                          placeholder="Enter location name"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Location Type"
                          options={LOCATION_TYPE_OPTIONS}
                          value={LOCATION_TYPE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Location Status"
                          options={LOCATION_STATUS_OPTIONS}
                          value={LOCATION_STATUS_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                  <Conditional condition={watchedType === "RETAIL"}>
                    <Grid2 size={12}>
                      <Controller
                        name="warehouse_id"
                        control={control}
                        rules={{ required: true }}
                        render={({ field, fieldState: { error } }) => (
                          <SelectLocationField
                            onChange={(value: any) => {
                              field.onChange(value)
                            }}
                            filterOptionsQuery={[
                              {
                                operator: FilterOperator.Equal,
                                field: "type",
                                value: "WAREHOUSE"
                              }
                            ]}
                            value={field.value}
                            label="Warehouse"
                            required
                            error={!!error}
                            helperText={error?.message}
                            placeholder="Select warehouse"
                          />
                        )}
                      />
                    </Grid2>
                  </Conditional>
                </Grid2>
              </Grid2>

              {/* Location Details Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <PublicIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Location Details
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="area"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Area"
                          placeholder="Enter area"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="countryId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <SelectCountryField
                          onChange={(option: any) => {
                            field.onChange(option)
                          }}
                          required
                          value={field.value}
                          label="Country"
                          placeholder="Select country"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="timezone"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={TIMEZONES}
                          value={field.value || null}
                          onChange={(_, value) => field.onChange(value ?? "")}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={t("locations.timezone")}
                              helperText={t("locations.timezoneHint")}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="cityId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState: { error } }) => (
                        <SelectCityField
                          onChange={(option: any) => {
                            field.onChange(option)
                          }}
                          required
                          value={field.value}
                          label="City"
                          placeholder="Select city"
                          countryId={watchedCountryId ?? ""}
                          disabled={!watchedCountryId}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
                {/* </Paper> */}
              </Grid2>

              {/* Tax Information Section */}
              <Grid2 size={12}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Tax Information (Optional)
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="taxNumber"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Tax Number"
                          placeholder="Enter tax number"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="salesTaxNumber"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Sales Tax Number"
                          placeholder="Enter sales tax number"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
                {/* </Paper> */}
              </Grid2>

              {/* Current Selection Display */}
              <Grid2 size={12}>
                <Fade in={isDirty}>
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      "& .MuiAlert-message": { width: "100%" }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight="bold">
                        Current Selection:
                      </Typography>
                      <Chip
                        icon={getTypeIcon(watchedType)}
                        label={watchedType === "WAREHOUSE" ? "Warehouse" : "Retail"}
                        color={getTypeColor(watchedType) as any}
                        size="small"
                      />
                      <Chip
                        label={watchedStatus === "ACTIVE" ? "Active" : "Inactive"}
                        color={getStatusColor(watchedStatus) as any}
                        size="small"
                      />
                    </Stack>
                  </Alert>
                </Fade>
              </Grid2>
            </Grid2>
          </form>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 3,
            background: theme.palette.background.default
            // backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50]
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={createLoading || updateLoading}
            startIcon={createLoading || updateLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              minWidth: 140,
              background: theme.palette.primary.main,
              "&:hover": {
                background: theme.palette.primary.dark
              }
            }}
          >
            {createLoading || updateLoading
              ? "Saving..."
              : formMode === "create"
                ? "Create Location"
                : "Update Location"}
          </Button>
        </DialogActions>
      </Conditional>
    </Dialog>
  )
}

export default EditUpdateLocationFormModal
